import { useMemo } from 'react';
/**
 * Hook that generates a pagination array for displaying page numbers in a UI component.
 *
 * @param currentPage - The currently active page number (1-indexed)
 * @param totalPages - The total number of pages available
 * @param visiblePages - The maximum number of consecutive pages to show (default: 5)
 * @returns An array of pagination items containing page numbers and ellipsis markers
 *
 * @example
 * const pages = usePagination(5, 20, 5);
 * // Returns: [1, 'ellipsis', 3, 4, 5, 6, 7, 'ellipsis', 20]
 *
 * The hook intelligently shows:
 * - All pages if total pages fit within the visible window
 * - Current page centered with visible pages around it
 * - Ellipsis markers where pages are skipped
 * - First and last page always visible when there are hidden pages
 */
export function usePagination(currentPage, totalPages, visiblePages = 5) {
    return useMemo(() => {
        // If all pages fit within the visible window, show them all
        if (totalPages <= visiblePages + 2) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }
        // Calculate offset to center the current page
        const half = Math.floor(visiblePages / 2);
        // Determine the start and end of the visible page range
        let start = Math.max(currentPage - half, 1);
        const end = Math.min(start + visiblePages - 1, totalPages);
        // Adjust start if end is near the total to maintain visiblePages count
        start = Math.max(end - visiblePages + 1, 1);
        const pages = [];
        // Add first page and ellipsis if there's a gap at the beginning
        if (start > 1) {
            pages.push(1);
            if (start > 2) {
                pages.push('ellipsis');
            }
        }
        // Add the consecutive visible pages
        for (let page = start; page <= end; page++) {
            pages.push(page);
        }
        // Add ellipsis and last page if there's a gap at the end
        if (end < totalPages) {
            if (end < totalPages - 1) {
                pages.push('ellipsis');
            }
            pages.push(totalPages);
        }
        return pages;
    }, [currentPage, totalPages, visiblePages]);
}
